import { Context } from '@nuxt/types'
import { Inject } from '@nuxt/types/app'

const CACHE_KEY = 'ds_icon_cache'
const CACHE_VERSION = 'v2'
const CACHE_MAX_AGE = 7 * 24 * 60 * 60 * 1000

interface CacheEntry {
  content: string
  timestamp: number
}

interface IconCache {
  version: string
  icons: Record<string, CacheEntry>
}

function getCache(): IconCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return { version: CACHE_VERSION, icons: {} }

    const cache = JSON.parse(raw) as IconCache
    if (cache.version !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY)
      return { version: CACHE_VERSION, icons: {} }
    }

    return cache
  } catch {
    return { version: CACHE_VERSION, icons: {} }
  }
}

function saveCache(cache: IconCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
  } catch {
    // Storage quota exceeded or unavailable
  }
}

function cleanExpiredEntries(cache: IconCache): IconCache {
  const now = Date.now()
  const cleaned: Record<string, CacheEntry> = {}

  for (const [name, entry] of Object.entries(cache.icons)) {
    if (now - entry.timestamp < CACHE_MAX_AGE) {
      cleaned[name] = entry
    }
  }

  return { version: cache.version, icons: cleaned }
}

export default (_context: Context, inject: Inject): void => {
  const memoryCache = new Map<string, string>()

  const iconCache = {
    get(name: string): string | null {
      if (memoryCache.has(name)) {
        return memoryCache.get(name) || null
      }

      const cache = getCache()
      const entry = cache.icons[name]

      if (entry && Date.now() - entry.timestamp < CACHE_MAX_AGE) {
        memoryCache.set(name, entry.content)
        return entry.content
      }

      return null
    },

    set(name: string, content: string): void {
      memoryCache.set(name, content)

      const cache = cleanExpiredEntries(getCache())
      cache.icons[name] = {
        content,
        timestamp: Date.now()
      }
      saveCache(cache)
    },

    has(name: string): boolean {
      return this.get(name) !== null
    },

    clear(): void {
      memoryCache.clear()
      localStorage.removeItem(CACHE_KEY)
    }
  }

  inject('iconCache', iconCache)
}

declare module 'vue/types/vue' {
  interface Vue {
    $iconCache: {
      get(name: string): string | null
      set(name: string, content: string): void
      has(name: string): boolean
      clear(): void
    }
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $iconCache: {
      get(name: string): string | null
      set(name: string, content: string): void
      has(name: string): boolean
      clear(): void
    }
  }
}
